import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { InviteStatus, type RegistrationInviteDto } from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../lib/i18n';
import {
  INVITE_STATUS_CLASS,
  useDeliveryLabels,
  useInviteStatusLabels,
  useFormatDate,
} from './status';

export function MyRequests() {
  const t = useT();
  const inviteStatusLabels = useInviteStatusLabels();
  const deliveryLabels = useDeliveryLabels();
  const formatDate = useFormatDate();
  const { isManager } = useAuth();
  const [rows, setRows] = useState<RegistrationInviteDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await (isManager ? api.listInvites() : api.listMyInvites()));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [isManager]);

  useEffect(() => {
    void load();
  }, [load]);

  async function cancel(id: string) {
    setBusyId(id);
    try {
      await api.cancelInvite(id);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>
            {isManager ? t('onboarding.requests.titleManager') : t('onboarding.requests.titleMine')}
          </h2>
          <p className="page-sub">
            {loading
              ? t('onboarding.requests.loading')
              : rows.length === 1
                ? t('onboarding.requests.countOne', { n: rows.length })
                : t('onboarding.requests.countOther', { n: rows.length })}
          </p>
        </div>
        <div className="spacer" />
        <Link to="/new">
          <button type="button">{t('onboarding.requests.newRegistration')}</button>
        </Link>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="erp-panel">
        {loading ? (
          <div className="panel-empty">{t('onboarding.requests.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="panel-empty is-empty">{t('onboarding.requests.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  {isManager && <th>{t('onboarding.requests.columns.requester')}</th>}
                  <th>{t('onboarding.requests.columns.recipient')}</th>
                  <th>{t('onboarding.requests.columns.method')}</th>
                  <th>{t('onboarding.requests.columns.status')}</th>
                  <th>{t('onboarding.requests.columns.created')}</th>
                  <th>{t('onboarding.requests.columns.expires')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    {isManager && <td>{r.requestedByName}</td>}
                    <td>
                      {r.submissionId ? (
                        <Link to={`/submissions/${r.submissionId}`}>
                          {r.targetEmail ?? t('onboarding.requests.viewSubmission')}
                        </Link>
                      ) : (
                        (r.targetEmail ?? '—')
                      )}
                    </td>
                    <td>{deliveryLabels[r.deliveryMethod]}</td>
                    <td>
                      <span className={INVITE_STATUS_CLASS[r.status]}>
                        {inviteStatusLabels[r.status]}
                      </span>
                    </td>
                    <td>{formatDate(r.createdAt)}</td>
                    <td>{formatDate(r.expiresAt)}</td>
                    <td>
                      {r.status === InviteStatus.Pending && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={busyId === r.id}
                          onClick={() => void cancel(r.id)}
                        >
                          {t('onboarding.requests.cancel')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
